import loadMujoco from "@mujoco/mujoco";
import type { MjData, MjModel, MjVFS, MainModule } from "@mujoco/mujoco";

export type MujocoModule = MainModule;

export interface G1Model {
  mujoco: MujocoModule;
  model: MjModel;
  data: MjData;
  jointIds: number[];
  qposIds: number[];
  dofIds: number[];
  forceLimits: number[];
  pelvisId: number;
  torsoId: number;
  vfs: MjVFS;
}

const JOINT_OBJECT = 3;
const BODY_OBJECT = 1;

/** Module-level caches so reloading a policy does not re-download WASM/STL. */
let mujocoPromise: Promise<MujocoModule> | null = null;
let assetsPromise: Promise<G1Assets> | null = null;

interface G1Assets {
  xml: string;
  meshes: Array<{ file: string; bytes: Uint8Array }>;
}

async function getMujoco(): Promise<MujocoModule> {
  if (!mujocoPromise) mujocoPromise = loadMujoco();
  return mujocoPromise;
}

async function getG1Assets(): Promise<G1Assets> {
  if (!assetsPromise) {
    assetsPromise = (async () => {
      const [xmlResponse, ...meshResponses] = await Promise.all([
        fetch("/robots/g1/g1.xml"),
        ...G1_MESHES.map((file) => fetch(`/robots/g1/meshes/${file}`)),
      ]);
      if (!xmlResponse.ok) throw new Error(`无法加载 G1 XML (${xmlResponse.status})`);
      if (meshResponses.some((response) => !response?.ok)) throw new Error("无法加载 G1 STL 资源");

      let xml = await xmlResponse.text();
      xml = xml.replace('meshdir="../meshes/g1"', 'meshdir="."');
      const meshes: Array<{ file: string; bytes: Uint8Array }> = [];
      for (const [index, file] of G1_MESHES.entries()) {
        const response = meshResponses[index];
        if (!response) throw new Error(`缺少 G1 STL 资源: ${file}`);
        meshes.push({ file, bytes: new Uint8Array(await response.arrayBuffer()) });
      }
      return { xml, meshes };
    })();
  }
  return assetsPromise;
}

function bindPolicyJoints(
  mujoco: MujocoModule,
  model: MjModel,
  policyJointNames: readonly string[],
): Pick<G1Model, "jointIds" | "qposIds" | "dofIds" | "forceLimits"> {
  if (policyJointNames.length !== 29) throw new Error("策略 metadata 必须包含 29 个关节");
  const jointIds = policyJointNames.map((name) => mujoco.mj_name2id(model, JOINT_OBJECT, name));
  if (jointIds.some((id) => id < 0)) throw new Error("G1 MJCF 缺少策略关节");
  const qposIds = jointIds.map((id) => model.jnt_qposadr[id] as number);
  const dofIds = jointIds.map((id) => model.jnt_dofadr[id] as number);
  const forceLimits = jointIds.map((id, index) => {
    const limit = (model.jnt_actfrcrange as Float64Array)[id * 2 + 1];
    if (limit === undefined) throw new Error(`缺少关节力矩限制: ${policyJointNames[index] ?? id}`);
    return Math.abs(limit);
  });
  return { jointIds, qposIds, dofIds, forceLimits };
}

/** Rebind joint index maps when swapping policies without rebuilding the MJCF. */
export function rebindPolicyJoints(sim: G1Model, policyJointNames: readonly string[]): void {
  const bound = bindPolicyJoints(sim.mujoco, sim.model, policyJointNames);
  sim.jointIds = bound.jointIds;
  sim.qposIds = bound.qposIds;
  sim.dofIds = bound.dofIds;
  sim.forceLimits = bound.forceLimits;
}

export async function createG1Model(policyJointNames: readonly string[]): Promise<G1Model> {
  const mujoco = await getMujoco();
  const assets = await getG1Assets();
  const vfs = new mujoco.MjVFS();
  for (const { file, bytes } of assets.meshes) {
    vfs.addBuffer(file, bytes);
  }
  const model = mujoco.MjModel.from_xml_string(assets.xml, vfs);
  const data = new mujoco.MjData(model);
  model.opt.timestep = 0.005;

  const bound = bindPolicyJoints(mujoco, model, policyJointNames);
  const pelvisId = mujoco.mj_name2id(model, BODY_OBJECT, "pelvis");
  const torsoId = mujoco.mj_name2id(model, BODY_OBJECT, "torso_link");
  if (pelvisId < 0 || torsoId < 0) throw new Error("G1 MJCF 缺少 pelvis 或 torso_link");
  return {
    mujoco,
    model,
    data,
    ...bound,
    pelvisId,
    torsoId,
    vfs,
  };
}

export function disposeG1(sim: G1Model | null): void {
  if (!sim) return;
  sim.data.delete();
  sim.model.delete();
  sim.vfs.delete();
}

export const G1_POLICY_JOINTS = [
  "left_hip_pitch_joint", "left_hip_roll_joint", "left_hip_yaw_joint", "left_knee_joint", "left_ankle_pitch_joint", "left_ankle_roll_joint",
  "right_hip_pitch_joint", "right_hip_roll_joint", "right_hip_yaw_joint", "right_knee_joint", "right_ankle_pitch_joint", "right_ankle_roll_joint",
  "waist_yaw_joint", "waist_roll_joint", "waist_pitch_joint",
  "left_shoulder_pitch_joint", "left_shoulder_roll_joint", "left_shoulder_yaw_joint", "left_elbow_joint", "left_wrist_roll_joint", "left_wrist_pitch_joint", "left_wrist_yaw_joint",
  "right_shoulder_pitch_joint", "right_shoulder_roll_joint", "right_shoulder_yaw_joint", "right_elbow_joint", "right_wrist_roll_joint", "right_wrist_pitch_joint", "right_wrist_yaw_joint",
];

const G1_MESHES = [
  "pelvis.STL", "pelvis_contour_link.STL", "left_hip_pitch_link.STL", "left_hip_roll_link.STL", "left_hip_yaw_link.STL", "left_knee_link.STL",
  "left_ankle_pitch_link.STL", "left_ankle_roll_link.STL", "right_hip_pitch_link.STL", "right_hip_roll_link.STL", "right_hip_yaw_link.STL", "right_knee_link.STL",
  "right_ankle_pitch_link.STL", "right_ankle_roll_link.STL", "waist_yaw_link_rev_1_0.STL", "waist_roll_link_rev_1_0.STL", "torso_link_rev_1_0.STL",
  "logo_link.STL", "head_link.STL", "left_shoulder_pitch_link.STL", "left_shoulder_roll_link.STL", "left_shoulder_yaw_link.STL", "left_elbow_link.STL",
  "left_wrist_roll_link.STL", "left_wrist_pitch_link.STL", "left_wrist_yaw_link.STL", "left_rubber_hand.STL", "right_shoulder_pitch_link.STL", "right_shoulder_roll_link.STL",
  "right_shoulder_yaw_link.STL", "right_elbow_link.STL", "right_wrist_roll_link.STL", "right_wrist_pitch_link.STL", "right_wrist_yaw_link.STL", "right_rubber_hand.STL",
];
